// Banner + modal de "posibles leads duplicados" (portado del CRM a Operations).
//
// El CRM tiene una feature estilo HubSpot que avisa de POSIBLES leads duplicados
// (misma persona/contacto) con un banner ámbar en la ficha; al pulsarlo abre un
// modal con los candidatos y badges del criterio de match (Mismo DNI / teléfono
// / email). En el CRM ese modal además permite FUSIONAR (acción peligrosa,
// reversible, survivor-wins). En Operations portamos SOLO la detección + listado
// — la fusión se queda en el CRM (deliberado). En su lugar cada candidato ofrece
// "Abrir ficha", que navega INTERNO a la ficha de Operations (/leads/:ulid).
//
// ENRIQUECIMIENTO (pedido por Yago): además de email/teléfono/market, mostramos
// el número de póliza y su estado. Ese dato NO vive en el CRM: sale del admin-api
// de Operations (GET /api/admin/operations/leads/:ulid/detail → policy.policyNumber
// + status del lead). Se resuelve AL ABRIR el modal (no antes), en paralelo para
// los pocos candidatos (≤10), con cache por sesión para no repetir fetches al
// reabrir. Si el detail de un candidato falla (p.ej. un lead que solo existe en
// el CRM), se muestra "—" sin romper el resto.
//
// Sesión CRM: el banner se envuelve en useCrmSession/connected — si no hay sesión
// CRM, NO se muestra nada (no pedimos conexión: es una ayuda, no una función
// crítica; por eso NO usamos CrmConnectGate aquí). Igualmente degrada en silencio
// si el endpoint falla (console.warn, sin UI de error).

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpError, request } from "../../../api/http";
import { useAuth } from "../../../context/AuthContext";
import { useCrmSession } from "../../../context/CrmSessionContext";
import {
  DuplicateCandidate,
  DuplicateMatchReason,
  MATCH_REASON_LABEL,
  getLeadDuplicates,
} from "../../../api/crmDuplicates";
import { LeadDetail } from "../../../types/leadDetail";
import { ModalShell } from "../../../components/FormControls";

// Enriquecimiento por candidato desde el admin-api de Operations.
type PolicyInfo = {
  policyNumber: string | null;
  status: string | null;
};

// Estado de carga del enriquecimiento de un candidato.
type PolicyState =
  | { kind: "loading" }
  | { kind: "loaded"; info: PolicyInfo }
  // No se pudo cargar el detail (lead solo en CRM, 404, etc.) → pintamos "—".
  | { kind: "missing" };

// Colores del badge por criterio: DNI es la señal fuerte (ámbar más intenso).
function reasonBadgeStyle(reason: DuplicateMatchReason): React.CSSProperties {
  const strong = reason === "dni";
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    borderRadius: "0.35rem",
    padding: "0.1rem 0.4rem",
    fontSize: "0.7rem",
    fontWeight: 600,
    background: strong ? "#fcd34d" : "#fef3c7",
    color: strong ? "#78350f" : "#92400e",
    whiteSpace: "nowrap",
  };
}

function displayEmail(email: string | null): string {
  return email && email.trim() ? email : "—";
}

function candidateName(c: DuplicateCandidate): string {
  return c.fullName || c.email || c.phone || "Lead sin nombre";
}

export default function DuplicateLeads({ detail }: { detail: LeadDetail }) {
  const { connected } = useCrmSession();
  const { token } = useAuth();
  const navigate = useNavigate();
  const leadUlid = detail.leadUlid;

  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Cache por sesión del enriquecimiento (policyNumber + estado) por leadUlid,
  // para no repetir fetches del admin-api al reabrir el modal. useRef → persiste
  // entre renders sin provocar re-render al escribir.
  const policyCacheRef = useRef<Map<string, PolicyInfo>>(new Map());
  const [policyStates, setPolicyStates] = useState<Map<string, PolicyState>>(
    new Map(),
  );

  // Detección de duplicados: solo si hay sesión CRM. Degrada en silencio (sin UI
  // de error): es una ayuda, no una función crítica.
  useEffect(() => {
    if (!connected || !leadUlid) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    getLeadDuplicates(leadUlid)
      .then((res) => {
        if (!cancelled) setCandidates(res.candidates);
      })
      .catch((err) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn("No se pudieron cargar los duplicados del CRM", err);
          setCandidates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [connected, leadUlid]);

  // Al abrir el modal: fetch en paralelo del detail de cada candidato (≤10) para
  // el enriquecimiento póliza+estado. Cache hit → se pinta al instante; miss →
  // placeholder "…" mientras carga; fallo → "—" (missing) sin romper.
  const loadPolicies = useCallback(() => {
    const cache = policyCacheRef.current;
    const next = new Map<string, PolicyState>();
    const toFetch: string[] = [];
    for (const c of candidates) {
      const cached = cache.get(c.id);
      if (cached) {
        next.set(c.id, { kind: "loaded", info: cached });
      } else {
        next.set(c.id, { kind: "loading" });
        toFetch.push(c.id);
      }
    }
    setPolicyStates(next);
    if (toFetch.length === 0) return;

    toFetch.forEach((id) => {
      request<LeadDetail>(
        `/api/admin/operations/leads/${encodeURIComponent(id)}/detail`,
        { token },
      )
        .then((d) => {
          const info: PolicyInfo = {
            policyNumber: d.policy?.policyNumber ?? null,
            status: typeof d.status === "string" ? d.status : null,
          };
          cache.set(id, info);
          setPolicyStates((prev) => {
            const m = new Map(prev);
            m.set(id, { kind: "loaded", info });
            return m;
          });
        })
        .catch((err) => {
          // Un lead que solo existe en el CRM da 404 en el admin-api: no es un
          // fallo real, solo no hay póliza en Operations → "—".
          if (!(err instanceof HttpError)) {
            // eslint-disable-next-line no-console
            console.warn("No se pudo enriquecer el duplicado", id, err);
          }
          setPolicyStates((prev) => {
            const m = new Map(prev);
            m.set(id, { kind: "missing" });
            return m;
          });
        });
    });
  }, [candidates, token]);

  function openModal() {
    setModalOpen(true);
    loadPolicies();
  }

  // Sin sesión CRM o sin candidatos → no se pinta nada (banner silencioso).
  if (!connected || candidates.length === 0) return null;

  const count = candidates.length;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.6rem",
          textAlign: "left",
          border: "1px solid #fcd34d",
          background: "#fffbeb",
          color: "#92400e",
          borderRadius: "0.5rem",
          padding: "0.6rem 0.85rem",
          marginBottom: "1rem",
          cursor: "pointer",
          fontSize: "0.9rem",
        }}
      >
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
        >
          <WarnIcon />
          <span style={{ fontWeight: 600 }}>
            {count === 1
              ? "1 posible lead duplicado"
              : `${count} posibles leads duplicados`}
          </span>
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
            fontSize: "0.8rem",
            fontWeight: 600,
          }}
        >
          Ver <ChevronIcon />
        </span>
      </button>

      {modalOpen && (
        <ModalShell
          title="Posibles leads duplicados"
          onClose={() => setModalOpen(false)}
          width={620}
        >
          <p
            style={{
              margin: "0 0 0.9rem",
              fontSize: "0.85rem",
              color: "#6b7280",
            }}
          >
            Estos leads comparten DNI, teléfono o email con el lead actual. El
            teléfono o email pueden pertenecer a un familiar, así que revisa antes
            de actuar. La fusión se realiza desde el CRM.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {candidates.map((c) => {
              const ps = policyStates.get(c.id);
              return (
                <div
                  key={c.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "0.5rem",
                    padding: "0.7rem 0.8rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: "#1f2933",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {candidateName(c)}
                    </div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#6b7280",
                        marginTop: "0.15rem",
                      }}
                    >
                      {[displayEmail(c.email), c.phone, c.market]
                        .filter((x) => x && x !== "—")
                        .join(" · ") || "—"}
                    </div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#374151",
                        marginTop: "0.3rem",
                      }}
                    >
                      <PolicyLine state={ps} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.3rem",
                        marginTop: "0.4rem",
                      }}
                    >
                      {c.matchReasons.map((r) => (
                        <span key={r} style={reasonBadgeStyle(r)}>
                          {MATCH_REASON_LABEL[r]}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setModalOpen(false);
                      navigate(`/leads/${c.id}`);
                    }}
                    style={{
                      flexShrink: 0,
                      alignSelf: "center",
                      border: "1px solid #93c5fd",
                      background: "white",
                      color: "#1d4ed8",
                      borderRadius: "0.4rem",
                      padding: "0.35rem 0.7rem",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Abrir ficha
                  </button>
                </div>
              );
            })}
          </div>
        </ModalShell>
      )}
    </>
  );
}

// Línea de enriquecimiento póliza + estado (o placeholder según el estado de
// carga del detail del candidato).
function PolicyLine({ state }: { state: PolicyState | undefined }) {
  if (!state || state.kind === "loading") {
    return <span style={{ color: "#9ca3af" }}>Póliza: cargando…</span>;
  }
  if (state.kind === "missing") {
    return (
      <span>
        Póliza: <strong>—</strong> · Estado: <strong>—</strong>
      </span>
    );
  }
  const { policyNumber, status } = state.info;
  return (
    <span>
      Póliza: <strong>{policyNumber || "—"}</strong> · Estado:{" "}
      <strong>{status || "—"}</strong>
    </span>
  );
}

function WarnIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
